import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import { Box, FormControl, FormHelperText, FormLabel } from '@mui/material'
import { getLabel } from './utils'

const EDITOR_MIN_HEIGHT_PX = 280

const EDITOR_CONFIG = {
  toolbar: {
    items: [
      'undo',
      'redo',
      '|',
      'heading',
      '|',
      'bold',
      'italic',
      '|',
      'link',
      'insertTable',
      '|',
      'bulletedList',
      'numberedList',
      'strikethrough',
      'subscript',
      'superscript',
      'blockQuote',
    ],
  },
}

export const CommonCkHtmlEditor = ({
  id,
  label,
  value = '',
  onChange,
  error,
  helperText,
  required = false,
  editorKey,
  disabled = false,
}) => {
  const resolvedEditorKey = useMemo(
    () => editorKey ?? id ?? 'ck-html-editor',
    [editorKey, id]
  )

  return (
    <FormControl fullWidth error={Boolean(error)}>
      {label ? (
        <FormLabel htmlFor={id} sx={{ mb: 1 }}>
          {getLabel(label, required)}
        </FormLabel>
      ) : null}
      <Box
        id={id}
        sx={{
          minHeight: EDITOR_MIN_HEIGHT_PX + 5,
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
          '& .ck-editor__editable': {
            minHeight: `${EDITOR_MIN_HEIGHT_PX}px`,
          },
          '& .ck.ck-editor__main > .ck-editor__editable:not(.ck-focused)': {
            borderColor: error ? 'error.main' : 'divider',
          },
        }}
      >
        <CKEditor
          key={resolvedEditorKey}
          editor={ClassicEditor}
          data={value}
          config={EDITOR_CONFIG}
          onChange={(event, editor) => onChange?.(editor.getData())}
          onReady={editor => {
            editor.editing.view.change(writer => {
              writer.setStyle(
                'min-height',
                `${EDITOR_MIN_HEIGHT_PX}px`,
                editor.editing.view.document.getRoot()
              )
            })
          }}
          disabled={disabled}
        />
      </Box>
      {error || helperText ? (
        <FormHelperText>{error || helperText}</FormHelperText>
      ) : null}
    </FormControl>
  )
}

CommonCkHtmlEditor.propTypes = {
  id: PropTypes.string,
  label: PropTypes.node,
  value: PropTypes.string,
  onChange: PropTypes.func,
  error: PropTypes.string,
  helperText: PropTypes.string,
  required: PropTypes.bool,
  editorKey: PropTypes.string,
  disabled: PropTypes.bool,
}
